/**
 * Pre-accession sequencing upgrades: add comparators (Duo/Trio) only within the same
 * product line (urWGS vs rWGS), driven by UI config parentSamplesRequired tiers.
 */

import type { HPOTerm } from '@/components/common/HPOSearch'
import type { FamilyMember, TestOptions } from '@/components/order/OrderTypes'
import { incidentalConsentGated } from '@/components/order/comparatorParentConsentConfig'
import type { UiConfig } from '@/config/uiConfig'
import {
  getLabName,
  getMaxParentSamplesForCategory,
  getPatientConsents,
  getTestTypeConfig,
  resolveTestTypeForCategoryTier,
} from '@/config/uiConfig'
import type { HPONode } from '@/features/terminology/hpo/hpoTypes'
import {
  mapSampleTypeToCollectionType,
  type CanonicalOrderPayload,
  type CanonicalParentInfo,
  type CanonicalPatient,
  type Comparator,
  type ComparatorDemographic,
  type ComparatorSecondaryConsent,
  type ComparatorSpecimen,
  type FamilyMemberRelationship,
} from '@/ordering/canonicalOrder'
import { getComparatorCapabilities } from '@/ordering/comparatorCapabilities'
import { isLeavingComparatorFamily } from '@/ordering/comparatorFamilyTransition'
import {
  deriveSelectedComparatorRelationships,
  getParentComparator,
  orderTypeFromComparators,
} from '@/ordering/comparatorReadAdapter'
import { selectionHasRelationship } from '@/ordering/testOptionsRelationships'
import type { AppDispatch } from '@/store'
import {
  mergeOrderSelection,
  mergePatient,
  removeComparator,
  setGuardianContact,
  setOrderType,
  upsertComparator,
} from '@/store/slices/canonicalOrderSlice'
import type { CategorizedHPOOption, IncidentalFindingsSelection, TestType } from '@/types/RequisitionFormTypes'
import { normalizePhenotypicTrait, PhenotypicTrait } from '@/types/RequisitionFormTypes'
import { FormCompletionChecker } from '@/utils/FormCompletionChecker'
import { mapCollectionStringToSampleTypeStrict } from '@/utils/orderFormUtils'

/** Duo / Trio upgrade targets for the current test type (same `category` in UI config). */
export function getAllowedComparatorUpgradeTestTypes(currentValue: TestType | string, config: UiConfig): TestType[] {
  const current = getComparatorCapabilities(currentValue, config)
  if (!current.requiresParentSamples && current.minComparators === 0 && current.category === '') return []
  const curN = current.minComparators
  const maxTier = getMaxParentSamplesForCategory(current.category, config)
  if (curN >= maxTier) return []
  const cat = current.category
  const TRF_COMPARATOR_ROW_CEILING = 3
  return config.testTypes
    .filter(
      (t) =>
        t.category === cat && t.parentSamplesRequired > curN && t.parentSamplesRequired <= TRF_COMPARATOR_ROW_CEILING
    )
    .map((t) => t.value)
}

/** All test types in the same product line (e.g. urWGS proband / duo / trio). */
export function getSameCategorySequencingTestTypes(currentValue: TestType | string, config: UiConfig): TestType[] {
  const current = getTestTypeConfig(currentValue as TestType, config)
  if (!current) return config.testTypes.map((t) => t.value)
  const cat = current.category
  return config.testTypes.filter((t) => t.category === cat).map((t) => t.value)
}

/**
 * Resolve the sequencing tier (`selectedOption` SKU + `orderType`) that matches
 * a comparator set, keeping the "Sequencing Options" label in lockstep with the
 * order. Shared by the OrderInProgress add / remove comparator flows so both
 * directions stay symmetric:
 *  - the **SKU tier** is the total number of *testing* comparators (ANY
 *    relationship — parents, siblings, extended relatives), capped at the
 *    category's max available tier, then mapped via
 *    `resolveTestTypeForCategoryTier`. This mirrors the draft (OrderStart) flow
 *    exactly: the "Trio (Proband + Two Comparators)" label counts comparator
 *    *samples*, not just biological parents, so adding a tested sibling upgrades
 *    the SKU. Falls back to the current `selectedOption` when the category has
 *    no SKU for that tier (a safe no-op);
 *  - the `orderType` (family structure) is derived purely from *biological
 *    parent* presence (`orderTypeFromComparators`) and is intentionally
 *    unaffected by non-parent comparators — matching OrderStart, where the tier
 *    and the family structure are computed independently.
 *
 * Pure: takes the current `selectedOption` and the post-mutation comparator set
 * and returns the resolved pair. The caller dispatches the resulting writes.
 */
export function resolveSequencingTierForComparators(
  currentSelectedOption: TestType | string | null | undefined,
  comparatorsAfter: ReadonlyArray<Comparator | null | undefined>,
  config: UiConfig
): { selectedOption: TestType | string | null; orderType: string } {
  const orderType = orderTypeFromComparators({ comparators: [...comparatorsAfter] } as CanonicalOrderPayload)
  const currentCategory = currentSelectedOption
    ? getTestTypeConfig(currentSelectedOption as TestType, config)?.category
    : undefined
  // SKU tier counts every testing comparator (any relationship), capped at the
  // category's max tier — NOT just parents. A tested sibling / aunt is a real
  // family sample that bills the higher tier (Duo/Trio = "Proband + N
  // Comparators"). Mirrors OrderStart's `testingComparatorCount` sync.
  const testingComparatorCount = comparatorsAfter.filter(
    (c) => c != null && c.isTestingComparator !== false && c.relationship !== 'PROBAND'
  ).length
  const maxTier = getMaxParentSamplesForCategory(currentCategory, config)
  const tier = Math.min(testingComparatorCount, maxTier)
  const resolvedSku = resolveTestTypeForCategoryTier(currentCategory, tier, config)
  return { selectedOption: resolvedSku ?? currentSelectedOption ?? null, orderType }
}

/**
 * Test types shown in the submitted-order sequencing modal: upgrade-only from what was last sent
 * to the backend (persisted tier), with two UX rules:
 * - Persisted **proband-only** → show the full same-category line (proband, duo, trio, quad).
 * - Persisted **duo** → persisted duo + upgrade targets (trio, quad), so an unsent local trio can be reverted to duo.
 * - Persisted **trio** or **quad** → persisted tier only. This modal selects biological parents
 *   only, so it cannot reach Quad; the Quad upgrade happens via side-nav Add Comparator.
 */
export function getInProgressSequencingModalAllowedTestTypes(
  persistedTestType: TestType | string,
  config: UiConfig
): TestType[] {
  const persisted = String(persistedTestType ?? '').trim()
  if (!persisted) return []

  if (!getTestTypeConfig(persisted as TestType, config)) return []
  const caps = getComparatorCapabilities(persisted, config)

  if (caps.minComparators === 0) {
    return getSameCategorySequencingTestTypes(persisted as TestType, config)
  }

  if (caps.minComparators === 1) {
    const out = new Set<TestType>()
    out.add(persisted as TestType)
    for (const u of getAllowedComparatorUpgradeTestTypes(persisted, config)) {
      out.add(u)
    }
    return [...out]
  }

  return [persisted as TestType]
}

export function testOptionsFromCanonicalOrder(canonical: CanonicalOrderPayload | null, config: UiConfig): TestOptions {
  const testType = (canonical?.order?.selectedOption ?? '') as TestType
  const cfg = testType ? getTestTypeConfig(testType, config) : undefined
  const parentSamplesRequired = cfg?.parentSamplesRequired ?? 0
  const ot = (canonical?.orderType ?? 'PROBAND_ONLY').trim()

  // P12 (MOTHER/FATHER migration): the comparator list is the source of truth
  // for the relationship selection. When it is present, read the relationships
  // directly. For legacy payloads with no comparators[] yet, fall back to
  // inferring the parent rows from `orderType` + the chosen test type's
  // `parentSamplesRequired` (preserves existing behavior pre-hydration).
  const fromComparators =
    canonical && Array.isArray(canonical.comparators) && canonical.comparators.length > 0
      ? deriveSelectedComparatorRelationships(canonical)
      : null
  let selectedComparatorRelationships: TestOptions['selectedComparatorRelationships']
  if (fromComparators) {
    selectedComparatorRelationships = fromComparators
  } else {
    const seeded: TestOptions['selectedComparatorRelationships'] = []
    if (parentSamplesRequired === 2) {
      seeded.push('MOTHER', 'FATHER')
    } else if (parentSamplesRequired === 1) {
      if (ot === 'FAMILY_DUO_MOTHER') seeded.push('MOTHER')
      else if (ot === 'FAMILY_DUO_FATHER') seeded.push('FATHER')
    }
    selectedComparatorRelationships = seeded
  }

  return {
    testType,
    otherFamilyMember: false,
    selectedComparatorRelationships,
    /**
     * Preserve "no selection" by leaving ``sampleType`` undefined when the proband's
     * collectionType has not yet been chosen on the canonical payload. Don't coerce to
     * 'whole-blood' — that would silently re-introduce the auto-default the modal is
     * trying to avoid for product lines that allow multiple proband sample types.
     * Use the strict mapper so unrecognized stored values surface as undefined too.
     */
    sampleType: mapCollectionStringToSampleTypeStrict(canonical?.patient?.collectionType ?? null) ?? undefined,
  }
}

/**
 * GEN-4471 Speed-switch block predicate (§5). Returns `true` when switching to `targetCategory`
 * is blocked because the current testing-comparator count exceeds that category's max tier.
 * Surface the reason to the user (e.g. "Rapid does not support 3 comparators; remove a comparator first").
 * Pure: no side effects, enforced in the UI selectability layer (modal speed-option selector).
 */
export function isSpeedSwitchBlocked(
  testingComparatorCount: number,
  targetCategory: string,
  config: UiConfig
): boolean {
  const targetMax = getMaxParentSamplesForCategory(targetCategory, config)
  return testingComparatorCount > targetMax
}

/**
 * GEN-4471 Speed-switch block reason. Returns a user-facing message when the switch is blocked,
 * or `null` when the switch is allowed.
 */
export function speedSwitchBlockReason(
  testingComparatorCount: number,
  targetCategory: string,
  config: UiConfig
): string | null {
  if (!isSpeedSwitchBlocked(testingComparatorCount, targetCategory, config)) {
    return null
  }
  const targetMax = getMaxParentSamplesForCategory(targetCategory, config)
  return `${targetCategory} supports at most ${targetMax} testing comparator${targetMax === 1 ? '' : 's'}. Remove a comparator to switch to this category.`
}

/**
 * Comparator parent slots and required count reflecting the placed order's family structure — not
 * just the test config. A FAMILY_DUO_* / FAMILY_TRIO order can be placed on a test whose config
 * ``parentSamplesRequired`` is 0 (one shared TestType for proband-only and family orders). Deriving
 * slots from config alone (via ``testOptionsFromCanonicalOrder``) would treat both comparators as
 * unselected and silently skip comparator validation. Mirror OrderInProgress's
 * ``parentSamplesForSequencingCard`` (``Math.max`` of config and family structure) so Send Updates
 * gating matches the parent cards the UI actually shows.
 */
export function familyAwareComparatorParentSlots(
  canonical: CanonicalOrderPayload | null,
  config: UiConfig
): { parentSamplesRequired: number; biologicalMother: boolean; biologicalFather: boolean } {
  const testType = (canonical?.order?.selectedOption ?? '') as TestType
  const cfg = testType ? getTestTypeConfig(testType, config) : undefined
  const fromConfig = cfg?.parentSamplesRequired ?? 0
  const ot = (canonical?.orderType ?? 'PROBAND_ONLY').trim()
  const fromFamilyStructure =
    ot === 'FAMILY_TRIO' ? 2 : ot === 'FAMILY_DUO_MOTHER' || ot === 'FAMILY_DUO_FATHER' ? 1 : 0
  const parentSamplesRequired = Math.max(fromFamilyStructure, fromConfig)
  let biologicalMother = false
  let biologicalFather = false
  if (parentSamplesRequired >= 2) {
    biologicalMother = true
    biologicalFather = true
  } else if (parentSamplesRequired === 1) {
    if (ot === 'FAMILY_DUO_MOTHER') biologicalMother = true
    else if (ot === 'FAMILY_DUO_FATHER') biologicalFather = true
  }
  return { parentSamplesRequired, biologicalMother, biologicalFather }
}

/**
 * When sending sequencing / comparator tier changes, each comparator requested by the
 * current test selection must be fully filled (same bar as OrderStart). MRN is required
 * only for EHR (Epic) flows; standalone web orders treat comparator MRN as optional.
 */
export function comparatorSectionsReadyForSequencingSend(
  canonical: CanonicalOrderPayload | null,
  config: UiConfig,
  options?: { mrnRequired?: boolean }
): boolean {
  if (!canonical) return false
  // GEN-3484: MRN is required to send on EHR (Epic) flows; callers may pass
  // `mrnRequired: false` to relax it for standalone web. Defaults to required to
  // preserve the comparator-refactor gating for callers that omit the option.
  const mrnRequired = options?.mrnRequired !== false
  const opts = testOptionsFromCanonicalOrder(canonical, config)
  const biologicalMother = selectionHasRelationship(opts, 'MOTHER')
  const biologicalFather = selectionHasRelationship(opts, 'FATHER')
  const testType = (canonical.order?.selectedOption ?? '') as TestType
  const cfg = testType ? getTestTypeConfig(testType, config) : undefined
  const parentSamplesRequired = cfg?.parentSamplesRequired ?? 0
  const parentReady = (required: boolean, parent: Comparator | null | undefined): boolean => {
    if (!required) return true
    if (!FormCompletionChecker.isBiologicalParentComplete(FormCompletionChecker.comparatorAsParentData(parent))) {
      return false
    }
    if (mrnRequired) return Boolean(parent?.demographic?.mrn?.trim())
    return true
  }
  if (!parentReady(biologicalMother, getParentComparator(canonical, 'mother'))) return false
  if (!parentReady(biologicalFather, getParentComparator(canonical, 'father'))) return false
  // Phase C (MOTHER/FATHER migration): FCC predicates take the selected-parent
  // relationship list instead of the retired biologicalMother/Father booleans.
  const selectedComparatorRelationships: FamilyMemberRelationship[] = [
    ...(biologicalMother ? (['MOTHER'] as const) : []),
    ...(biologicalFather ? (['FATHER'] as const) : []),
  ]
  if (
    !FormCompletionChecker.isCanonicalIncidentalFindingsSubmittable(canonical, {
      incidentalApplicable: incidentalConsentGated(getPatientConsents(config)),
      parentSamplesRequired,
      selectedComparatorRelationships,
    })
  ) {
    return false
  }
  return FormCompletionChecker.isCanonicalComparatorResearchContactSatisfied(canonical, {
    parentSamplesRequired,
    selectedComparatorRelationships,
  })
}

/** `order.selectedOption` from ``serializeSequencingBaseline`` JSON (OrderInProgress baseline). */
export function persistedTestTypeFromSequencingBaselineSerialized(serialized: string | null): string {
  if (!serialized?.trim()) return ''
  try {
    const o = JSON.parse(serialized) as { t?: string }
    return String(o.t ?? '').trim()
  } catch {
    return ''
  }
}

/** Rebuild sequencing modal ``TestOptions`` from last-sent baseline JSON (not live Redux). */
export function testOptionsFromSequencingBaselineSerialized(
  serialized: string | null,
  config: UiConfig
): TestOptions | null {
  if (!serialized?.trim()) return null
  try {
    const o = JSON.parse(serialized) as { t?: string; ot?: string; col?: string | null }
    const shell = {
      orderType: String(o.ot ?? 'PROBAND_ONLY').trim(),
      order: { selectedOption: (o.t ? String(o.t).trim() : null) as TestType | null },
      patient: { collectionType: o.col ?? null },
    } as CanonicalOrderPayload
    return testOptionsFromCanonicalOrder(shell, config)
  } catch {
    return null
  }
}

export function createEmptyBiologicalFamilyMember(
  testingRequested: boolean,
  sampleType: FamilyMember['info']['sampleType'] = undefined,
  /** Duo (one comparator): default the tested parent as primary guardian. */
  isPrimaryGuardian = false
): FamilyMember {
  return {
    testingRequested,
    isPrimaryGuardian,
    incidentalFindingsSelection: null,
    newYorkSampleRetentionOptIn: false,
    researchContactConsent: false,
    info: {
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      medicalRecordNumber: '',
      sampleType,
      email: '',
      phone: '',
      phenotypicTrait: PhenotypicTrait.Unknown,
      clinicalNotes: '',
      phenotypes: [] as HPOTerm[],
    },
  }
}

function mapCategoryToHpoType(category: string): HPOTerm['type'] {
  if (category === 'Key') return 'key'
  if (category === 'Additional') return 'additional'
  return 'excluded'
}

function hpoTermsToHpOTerms(terms: unknown): HPOTerm[] {
  if (!Array.isArray(terms)) return []
  const out: HPOTerm[] = []
  for (const raw of terms) {
    const t = raw as { id?: string; label?: string; category?: string }
    if (!t?.id || !t?.label) continue
    out.push({
      id: t.id,
      name: t.label,
      hpoCode: t.id,
      type: mapCategoryToHpoType(String(t.category ?? 'Key')),
    })
  }
  return out
}

/**
 * Comparator consent fields on `mother` / `father` are optional in persisted payloads; some
 * sessions only store secondary consents on `patient`. When parent rows omit those fields, mirror
 * proband values for NY retention so checkboxes match sparse GET responses. Research contact consent
 * is independent per person and is not mirrored from the proband.
 *
 * Incidental findings are **not** mirrored when the parent row exists but omits
 * ``includeIncidentalFindings`` (except proband opt-out, which always forces comparator opt-out).
 * Copying proband opt-in would falsely pre-check the first IF option; RCIGM lab copy treats
 * unset comparator IF as no selection. Proband IF fallback applies only when there is no
 * parent row yet (empty comparator slot).
 */
function applyProbandConsentFallbackToFamilyMember(
  member: FamilyMember,
  parent: Comparator | null | undefined,
  proband: CanonicalPatient | null | undefined
): FamilyMember {
  if (!proband) {
    return member
  }

  const sc = parent?.secondaryConsent
  // GEN-3484: proband opt-out of incidental findings forces every comparator to opt-out.
  const probandLocksComparatorIf = proband.incidentalFindingsSelection === 'opt-out'

  const incidentalExplicitOnParent =
    parent != null && (sc?.includeIncidentalFindings === true || sc?.includeIncidentalFindings === false)

  const nyExplicitOnParent = parent != null && sc?.newYorkSampleRetentionOptIn != null
  const researchExplicitOnParent = parent != null && sc?.researchContactConsent != null

  return {
    ...member,
    incidentalFindingsSelection: probandLocksComparatorIf
      ? 'opt-out'
      : incidentalExplicitOnParent
        ? member.incidentalFindingsSelection
        : parent == null
          ? (proband.incidentalFindingsSelection ?? member.incidentalFindingsSelection)
          : member.incidentalFindingsSelection,
    newYorkSampleRetentionOptIn: nyExplicitOnParent
      ? member.newYorkSampleRetentionOptIn
      : proband.newYorkSampleRetentionOptIn === true,
    researchContactConsent: researchExplicitOnParent
      ? member.researchContactConsent
      : proband.researchContactConsent === true,
  }
}

export function canonicalParentToFamilyMember(
  parent: Comparator | null | undefined,
  testingRequested: boolean,
  probandConsentFallback?: CanonicalPatient | null | undefined
): FamilyMember {
  const base = !parent
    ? createEmptyBiologicalFamilyMember(testingRequested, undefined, false)
    : (() => {
        const inc = parent.secondaryConsent?.includeIncidentalFindings
        const incidentalFindingsSelection: IncidentalFindingsSelection =
          inc === true ? 'opt-in' : inc === false ? 'opt-out' : null
        /** Prefer the parent's own persisted collectionType so user-selected sample type isn't clobbered
         *  by the proband's sample type on every canonical refresh. */
        const persistedParentSampleType = mapCollectionStringToSampleTypeStrict(parent.specimen?.collectionType ?? null)
        const demo = parent.demographic
        return {
          testingRequested,
          isPrimaryGuardian: parent.isPrimaryGuardian === true,
          incidentalFindingsSelection,
          newYorkSampleRetentionOptIn: parent.secondaryConsent?.newYorkSampleRetentionOptIn === true,
          researchContactConsent: parent.secondaryConsent?.researchContactConsent === true,
          info: {
            firstName: demo?.firstName ?? '',
            lastName: demo?.lastName ?? '',
            dateOfBirth: demo?.dateOfBirth ?? '',
            medicalRecordNumber: demo?.mrn ?? '',
            sampleType: persistedParentSampleType ?? undefined,
            email: demo?.email ?? '',
            phone: demo?.phone ?? '',
            phenotypicTrait: normalizePhenotypicTrait(parent.phenotypicTrait),
            clinicalNotes: parent.clinicalNote?.trim() ?? '',
            phenotypes: hpoTermsToHpOTerms(parent.hpoTerms),
          },
        }
      })()

  return applyProbandConsentFallbackToFamilyMember(base, parent, probandConsentFallback)
}

function mapPhenotypesToCategorized(terms: HPOTerm[], source: 'phenoml' | 'manual' = 'manual'): CategorizedHPOOption[] {
  return terms.map((term) => ({
    id: term.id,
    label: term.name,
    node: { id: term.id, lbl: term.name, type: 'PhenotypicFeature' } as HPONode,
    category: term.type === 'key' ? 'Key' : term.type === 'additional' ? 'Additional' : 'Excluded',
    source,
  }))
}

/**
 * FamilyMember rows for TestOptionsModal comparator confirmation dialogs,
 * keyed by `relationship`. P8 (MOTHER/FATHER migration): replaces the
 * previous `{ biologicalMotherData, biologicalFatherData }` slot pair so the
 * modal can stay list-driven. The return is a relationship-keyed partial
 * record (Phase B, step 6) rather than a MOTHER/FATHER-only pair so callers /
 * consumers are not locked to the two-slot shape; only MOTHER / FATHER rows
 * are populated today because those are the only relationships the modal's
 * confirm-on-uncheck prompts apply to.
 */
export function familyMembersForTestOptionsModal(
  canonical: CanonicalOrderPayload | null,
  prev: TestOptions
): Partial<Record<FamilyMemberRelationship, FamilyMember>> {
  if (!canonical) return {}
  const biologicalMother = selectionHasRelationship(prev, 'MOTHER')
  const biologicalFather = selectionHasRelationship(prev, 'FATHER')
  return {
    MOTHER: biologicalMother
      ? canonicalParentToFamilyMember(getParentComparator(canonical, 'mother'), true, canonical.patient)
      : createEmptyBiologicalFamilyMember(false),
    FATHER: biologicalFather
      ? canonicalParentToFamilyMember(getParentComparator(canonical, 'father'), true, canonical.patient)
      : createEmptyBiologicalFamilyMember(false),
  }
}

export function familyMemberToCanonicalParent(
  member: FamilyMember,
  parentType: 'mother' | 'father',
  existing: CanonicalParentInfo | null | undefined
): CanonicalParentInfo {
  return {
    parentType,
    parentFirstName: member.info.firstName,
    parentLastName: member.info.lastName,
    parentDateOfBirth: member.info.dateOfBirth,
    parentMRN: member.info.medicalRecordNumber,
    parentPhone: member.info.phone,
    parentEmail: member.info.email,
    phenotypicTrait: member.info.phenotypicTrait,
    hpoTerms: mapPhenotypesToCategorized(member.info.phenotypes, 'manual'),
    icd10Terms: '',
    clinicalNote: member.info.clinicalNotes?.trim() ? member.info.clinicalNotes.trim() : null,
    isPrimaryGuardian: member.isPrimaryGuardian === true,
    includeIncidentalFindings:
      member.incidentalFindingsSelection === 'opt-in'
        ? true
        : member.incidentalFindingsSelection === 'opt-out'
          ? false
          : null,
    newYorkSampleRetentionOptIn: member.newYorkSampleRetentionOptIn === true,
    researchContactConsent: member.researchContactConsent === true,
    collectionType: mapSampleTypeToCollectionType(member.info.sampleType) || null,
    specimenId: existing?.specimenId ?? null,
    demographicId: existing?.demographicId ?? null,
  }
}

/**
 * Comparator-shaped sibling of {@link familyMemberToCanonicalParent}. Builds a
 * `Comparator` row from the UI-layer `FamilyMember` shape, preserving the
 * existing comparator's `id` / `demographic.demographicId` / `specimen.specimenId`
 * for stable upserts (so `upsertComparator` updates in place rather than
 * appending a duplicate row). Field mapping mirrors `familyMemberToCanonicalParent`
 * so the legacy slot and the new comparator stay in lockstep across the Plan A.2
 * bridge — see `applyComparatorUpsertToLegacyParentSlot` for the reverse direction.
 */
export function familyMemberToComparator(
  member: FamilyMember,
  relationship: 'MOTHER' | 'FATHER',
  existing: Comparator | null | undefined
): Comparator {
  const demographic: ComparatorDemographic = {
    firstName: member.info.firstName,
    lastName: member.info.lastName,
    dateOfBirth: member.info.dateOfBirth,
    mrn: member.info.medicalRecordNumber,
    email: member.info.email,
    phone: member.info.phone,
    demographicId: existing?.demographic?.demographicId ?? null,
  }
  const specimen: ComparatorSpecimen = {
    specimenId: existing?.specimen?.specimenId ?? null,
    collectionType: mapSampleTypeToCollectionType(member.info.sampleType) || null,
  }
  const secondaryConsent: ComparatorSecondaryConsent = {
    includeIncidentalFindings:
      member.incidentalFindingsSelection === 'opt-in'
        ? true
        : member.incidentalFindingsSelection === 'opt-out'
          ? false
          : null,
    newYorkSampleRetentionOptIn: member.newYorkSampleRetentionOptIn === true,
    researchContactConsent: member.researchContactConsent === true,
  }
  const out: Comparator = {
    relationship,
    demographic,
    specimen,
    isPrimaryGuardian: member.isPrimaryGuardian === true,
    // GEN-3152: carry the member's testing intent onto the row. Leaving the key
    // unset serialises as null and reads as "no testing" downstream, which
    // silently dropped a sequenced primary-guardian parent from the trio.
    isTestingComparator: member.testingRequested !== false,
    secondaryConsent,
    phenotypicTrait: member.info.phenotypicTrait,
    hpoTerms: mapPhenotypesToCategorized(member.info.phenotypes, 'manual'),
    icd10Terms: '',
    clinicalNote: member.info.clinicalNotes?.trim() ? member.info.clinicalNotes.trim() : null,
  }
  if (existing?.id) out.id = existing.id
  return out
}

/**
 * Consent fields for a comparator FamilyMember, read straight off the canonical
 * parent slot. Unlike {@link canonicalParentToFamilyMember}, this intentionally
 * does NOT apply the proband consent fallback — it preserves the historical
 * OrderStart hydration semantics (a parent that omits NY-retention / research
 * consent defaults to `false`, never mirrors the proband). Behavior-preserving
 * for the OrderStart parent-state derivation; do not "fix" the fallback here
 * without an explicit product decision (it would change consent display).
 */
export function consentFromCanonicalParent(
  parent?: Comparator | null
): Pick<FamilyMember, 'incidentalFindingsSelection' | 'newYorkSampleRetentionOptIn' | 'researchContactConsent'> {
  const inc = parent?.secondaryConsent?.includeIncidentalFindings
  const incidentalFindingsSelection: IncidentalFindingsSelection | null =
    inc === true ? 'opt-in' : inc === false ? 'opt-out' : null
  return {
    incidentalFindingsSelection,
    newYorkSampleRetentionOptIn: parent?.secondaryConsent?.newYorkSampleRetentionOptIn === true,
    researchContactConsent: parent?.secondaryConsent?.researchContactConsent === true,
  }
}

/**
 * Resolve the primary-guardian checkbox for a parent slot from canonical, using
 * the same precedence as `normalizeCanonicalOrderForSessionPersist` (trio:
 * father wins if both true; default mother). An unselected slot is never the
 * guardian; a duo's single present parent defaults to guardian when canonical
 * leaves the flag unset.
 */
function primaryGuardianForHydrate(
  parentSamplesRequired: number,
  slot: 'mother' | 'father',
  motherSelected: boolean,
  fatherSelected: boolean,
  motherComp: Comparator | null | undefined,
  fatherComp: Comparator | null | undefined
): boolean {
  const selected = slot === 'mother' ? motherSelected : fatherSelected
  if (!selected) return false
  if (parentSamplesRequired >= 2 && motherSelected && fatherSelected) {
    // Single-primary-guardian rule (MOTHER/FATHER migration P5): in a
    // multi-parent order, exactly one parent is the guardian. Parent
    // comparators are appended in order (mother, then father), so when either
    // flag is true the LAST flagged parent wins ("father wins both"), and when
    // neither flag is true we fall through to the FIRST parent comparator
    // ("default mother"). This is the list-driven form of the same invariant
    // that `normalizeCanonicalOrderForSessionPersist` enforces on persist.
    const motherFlag = motherComp?.isPrimaryGuardian === true
    const fatherFlag = fatherComp?.isPrimaryGuardian === true
    if (motherFlag || fatherFlag) {
      // Last flagged wins; iteration order is [mother, father].
      return slot === 'mother' ? motherFlag && !fatherFlag : fatherFlag
    }
    // Neither flagged: first parent (mother) is the guardian default.
    return slot === 'mother'
  }
  const parent = slot === 'mother' ? motherComp : fatherComp
  if (parent?.isPrimaryGuardian === true) return true
  if (parent?.isPrimaryGuardian === false) return false
  return parentSamplesRequired === 1 && Boolean(parent)
}

/** Identity/specimen `info` for a comparator FamilyMember from a parent comparator. */
function canonicalParentInfoForHydrate(parent?: Comparator | null): Partial<FamilyMember['info']> | undefined {
  if (!parent) return undefined
  const demo = parent.demographic
  return {
    firstName: demo?.firstName ?? '',
    lastName: demo?.lastName ?? '',
    dateOfBirth: demo?.dateOfBirth ?? '',
    medicalRecordNumber: demo?.mrn ?? '',
    email: demo?.email ?? '',
    phone: demo?.phone ?? '',
    phenotypicTrait: normalizePhenotypicTrait(parent.phenotypicTrait),
    clinicalNotes: parent.clinicalNote?.trim() ?? '',
    phenotypes:
      (parent.hpoTerms as CategorizedHPOOption[] | undefined)?.map((term) => ({
        id: term.id,
        name: term.label,
        hpoCode: term.id,
        type: mapCategoryToHpoType(term.category),
      })) ?? [],
    sampleType: mapCollectionStringToSampleTypeStrict(parent.specimen?.collectionType ?? null) ?? undefined,
  }
}

/**
 * Derive the OrderStart `biologicalMother` / `biologicalFather` UI state directly
 * from canonical + the current `TestOptions`, replacing the prior local React
 * state that was hydrated imperatively from canonical. This is behavior-
 * preserving: it reproduces exactly what the OrderStart hydration effects
 * computed (testing flag from the selection, primary-guardian via
 * {@link primaryGuardianForHydrate}, consent via
 * {@link consentFromCanonicalParent} with no proband fallback, and identity
 * `info` from the parent comparator). An unselected side yields a fresh,
 * non-testing empty member.
 */
export function deriveBiologicalParentsFromCanonical(
  canonical: CanonicalOrderPayload | null,
  testOptions: TestOptions,
  config: UiConfig
): { biologicalMother: FamilyMember; biologicalFather: FamilyMember } {
  const motherSelected = selectionHasRelationship(testOptions, 'MOTHER')
  const fatherSelected = selectionHasRelationship(testOptions, 'FATHER')
  const cfg = testOptions.testType ? getTestTypeConfig(testOptions.testType, config) : undefined
  const parentSamplesRequired = cfg?.parentSamplesRequired ?? 0

  // GEN-3393: parent rows are sourced directly from comparators[].
  const motherComp = getParentComparator(canonical, 'mother')
  const fatherComp = getParentComparator(canonical, 'father')

  const buildSide = (
    slot: 'mother' | 'father',
    selected: boolean,
    parent: Comparator | null | undefined
  ): FamilyMember => {
    const base = createEmptyBiologicalFamilyMember(false)
    if (!selected) return base
    return {
      ...base,
      testingRequested: true,
      isPrimaryGuardian: primaryGuardianForHydrate(
        parentSamplesRequired,
        slot,
        motherSelected,
        fatherSelected,
        motherComp,
        fatherComp
      ),
      ...consentFromCanonicalParent(parent),
      info: { ...base.info, ...(canonicalParentInfoForHydrate(parent) ?? {}) },
    }
  }

  return {
    biologicalMother: buildSide('mother', motherSelected, motherComp),
    biologicalFather: buildSide('father', fatherSelected, fatherComp),
  }
}

/**
 * Apply sequencing / comparator changes from TestOptionsModal on the in-progress
 * order (Redux canonical only — mirrors OrderStart ``TestOptionsModal`` ``onSave``),
 * including upgrades, downgrades (e.g. trio → duo → proband), and same-tier comparator tweaks.
 */
export function applySequencingOptionsChangeToCanonical(params: {
  dispatch: AppDispatch
  canonical: CanonicalOrderPayload | null
  prev: TestOptions
  next: TestOptions
  config: UiConfig
  /**
   * Parent sides whose comparator row the caller persists itself (the R5
   * "Add Comparator" flow upserts the user-authored comparator right after
   * this call). For those sides we skip the empty-parent SEED write below —
   * that seed defaults `isPrimaryGuardian: true` for a duo (single comparator),
   * which both overwrote the user's just-authored row and cleared the
   * standalone patient Guardian Contact card (the reducer's
   * single-primary-guardian invariant nulls `guardianContact` on any
   * primary-guardian upsert). The caller's comparator carries the user's actual
   * primary-guardian choice, so seeding here is redundant and destructive.
   * Tier/orderType resolution, deselect removals, and trio primary-guardian
   * promotion still run. Omit (default) to preserve the TestOptionsModal toggle
   * flow, which has no caller-supplied row and relies on the seed.
   */
  callerProvidedParentSides?: ReadonlyArray<'mother' | 'father'>
}): TestType | string | null {
  const { dispatch, canonical, prev, next, config, callerProvidedParentSides } = params
  if (!canonical) return null

  const callerSeedsMother = callerProvidedParentSides?.includes('mother') === true
  const callerSeedsFather = callerProvidedParentSides?.includes('father') === true

  // C2.1b.2: write-side migration. We dispatch `upsertComparator` /
  // `removeComparator` instead of the legacy `setMother` / `setFather` actions.
  // Capture the existing parent comparators once so each upsert preserves the
  // stable `id` (and embedded specimenId/demographicId) — without this, the
  // reducer would mint a new id and `upsertComparator` would APPEND a duplicate
  // parent row instead of updating in place. The Plan A.2 bridge in the slice
  // keeps the legacy `mother` / `father` slots in sync with these writes, so
  // any read site still on the legacy slot is unaffected.
  const existingMotherComp = getParentComparator(canonical, 'mother')
  const existingFatherComp = getParentComparator(canonical, 'father')

  const prevSel = {
    biologicalMother: selectionHasRelationship(prev, 'MOTHER'),
    biologicalFather: selectionHasRelationship(prev, 'FATHER'),
  }
  const nextSel = {
    biologicalMother: selectionHasRelationship(next, 'MOTHER'),
    biologicalFather: selectionHasRelationship(next, 'FATHER'),
  }

  // R5 sequencing decoupling: the Sequencing Options modal selects a *speed*
  // (category) that resolves to the category's proband-only SKU, while the
  // family structure (comparators) is chosen separately. The submitted SKU must
  // follow the comparator-derived tier — a duo family bills the duo SKU, not the
  // proband SKU it resolved to on speed selection — and the reconciliation below
  // keys off `newParentSamplesRequired` (duo guardian defaulting, trio
  // promotion, etc.), so it must see the *requested* tier. Re-resolve
  // `next.testType` to the same-category SKU whose tier matches the requested
  // parent count before computing anything tier-dependent. No-op for callers
  // (OrderInProgress) that already pass the tier-correct SKU, and a safe no-op
  // when the category lacks that tier (keeps the caller's selection).
  const requestedParentCount = (nextSel.biologicalMother ? 1 : 0) + (nextSel.biologicalFather ? 1 : 0)
  const nextCategory = next.testType ? getTestTypeConfig(next.testType, config)?.category : undefined
  const effectiveNextTestType =
    resolveTestTypeForCategoryTier(nextCategory, requestedParentCount, config) ?? next.testType
  const effectiveNext: TestOptions =
    effectiveNextTestType === next.testType ? next : { ...next, testType: effectiveNextTestType }

  const prevCfg = prev.testType ? getTestTypeConfig(prev.testType, config) : undefined
  const newCfg = effectiveNext.testType ? getTestTypeConfig(effectiveNext.testType, config) : undefined
  const prevParentSamplesRequired = prevCfg?.parentSamplesRequired ?? 0
  const newParentSamplesRequired = newCfg?.parentSamplesRequired ?? 0
  const leavingComparatorFamily = isLeavingComparatorFamily(prevParentSamplesRequired, newParentSamplesRequired)

  const singleComparatorOnly = newParentSamplesRequired === 1

  const biologicalMother = prevSel.biologicalMother
    ? canonicalParentToFamilyMember(existingMotherComp, true, canonical.patient)
    : createEmptyBiologicalFamilyMember(false)
  const biologicalFather = prevSel.biologicalFather
    ? canonicalParentToFamilyMember(existingFatherComp, true, canonical.patient)
    : createEmptyBiologicalFamilyMember(false)

  dispatch(
    mergeOrderSelection({
      selectedOption: effectiveNext.testType || null,
      labName: getLabName(config) || null,
    })
  )
  // Only push proband ``collectionType`` to canonical when the user actually
  // changed the sample type. Re-writing it on every modal save risks clobbering
  // the persisted/collected sample type if ``next.sampleType`` ever drifts
  // (e.g. an upstream auto-coercion in the modal) — that drift would create
  // duplicate ``specimen`` / ``order_item`` rows server-side on the PUT.
  if (prev.sampleType !== next.sampleType) {
    dispatch(mergePatient({ collectionType: mapSampleTypeToCollectionType(next.sampleType) || null }))
  }

  if (leavingComparatorFamily) {
    if (existingMotherComp?.id) dispatch(removeComparator(existingMotherComp.id))
    if (existingFatherComp?.id) dispatch(removeComparator(existingFatherComp.id))
    dispatch(setGuardianContact(null))
    dispatch(setOrderType('PROBAND_ONLY'))
    return effectiveNext.testType || null
  }

  if (!nextSel.biologicalMother && biologicalMother.testingRequested) {
    if (existingMotherComp?.id) dispatch(removeComparator(existingMotherComp.id))
  }
  if (!nextSel.biologicalFather && biologicalFather.testingRequested) {
    if (existingFatherComp?.id) dispatch(removeComparator(existingFatherComp.id))
  }

  if (nextSel.biologicalMother && !biologicalMother.testingRequested && !callerSeedsMother) {
    const nextMother = singleComparatorOnly
      ? createEmptyBiologicalFamilyMember(true, undefined, true)
      : { ...biologicalMother, testingRequested: true }
    if (!existingMotherComp || singleComparatorOnly) {
      dispatch(upsertComparator(familyMemberToComparator(nextMother, 'MOTHER', existingMotherComp)))
    }
  }
  if (nextSel.biologicalFather && !biologicalFather.testingRequested && !callerSeedsFather) {
    const nextFather = singleComparatorOnly
      ? createEmptyBiologicalFamilyMember(true, undefined, true)
      : { ...biologicalFather, testingRequested: true }
    if (!existingFatherComp || singleComparatorOnly) {
      dispatch(upsertComparator(familyMemberToComparator(nextFather, 'FATHER', existingFatherComp)))
    }
  }

  if (
    !leavingComparatorFamily &&
    newParentSamplesRequired === 2 &&
    nextSel.biologicalMother &&
    nextSel.biologicalFather &&
    prevParentSamplesRequired <= 1
  ) {
    const motherEffective =
      nextSel.biologicalMother && !biologicalMother.testingRequested
        ? singleComparatorOnly
          ? createEmptyBiologicalFamilyMember(true, undefined, true)
          : { ...biologicalMother, testingRequested: true }
        : biologicalMother
    const fatherEffective =
      nextSel.biologicalFather && !biologicalFather.testingRequested
        ? singleComparatorOnly
          ? createEmptyBiologicalFamilyMember(true, undefined, true)
          : { ...biologicalFather, testingRequested: true }
        : biologicalFather
    const wasDuoFatherOnly = prevSel.biologicalFather && !prevSel.biologicalMother
    const keepFatherPrimary =
      prevParentSamplesRequired === 1 && wasDuoFatherOnly && fatherEffective.isPrimaryGuardian === true
    if (keepFatherPrimary) {
      const nextM = { ...motherEffective, isPrimaryGuardian: false }
      const nextF = { ...fatherEffective, isPrimaryGuardian: true }
      dispatch(upsertComparator(familyMemberToComparator(nextM, 'MOTHER', existingMotherComp)))
      dispatch(upsertComparator(familyMemberToComparator(nextF, 'FATHER', existingFatherComp)))
    } else {
      const nextM = { ...motherEffective, isPrimaryGuardian: true }
      const nextF = { ...fatherEffective, isPrimaryGuardian: false }
      dispatch(upsertComparator(familyMemberToComparator(nextM, 'MOTHER', existingMotherComp)))
      dispatch(upsertComparator(familyMemberToComparator(nextF, 'FATHER', existingFatherComp)))
    }
  }

  if (!leavingComparatorFamily && prevParentSamplesRequired === 2 && newParentSamplesRequired === 1) {
    if (nextSel.biologicalMother && biologicalMother.testingRequested) {
      const nextMother = { ...biologicalMother, isPrimaryGuardian: true }
      if (existingMotherComp) {
        dispatch(upsertComparator(familyMemberToComparator(nextMother, 'MOTHER', existingMotherComp)))
      }
    }
    if (nextSel.biologicalFather && biologicalFather.testingRequested) {
      const nextFather = { ...biologicalFather, isPrimaryGuardian: true }
      if (existingFatherComp) {
        dispatch(upsertComparator(familyMemberToComparator(nextFather, 'FATHER', existingFatherComp)))
      }
    }
    if (nextSel.biologicalMother && biologicalFather.isPrimaryGuardian) {
      const nextFather = { ...biologicalFather, isPrimaryGuardian: false }
      if (existingFatherComp) {
        dispatch(upsertComparator(familyMemberToComparator(nextFather, 'FATHER', existingFatherComp)))
      }
    }
    if (nextSel.biologicalFather && biologicalMother.isPrimaryGuardian) {
      const nextMother = { ...biologicalMother, isPrimaryGuardian: false }
      if (existingMotherComp) {
        dispatch(upsertComparator(familyMemberToComparator(nextMother, 'MOTHER', existingMotherComp)))
      }
    }
  }

  // P12 (MOTHER/FATHER migration): derive the new tier directly from the
  // requested parent selection without going through the boolean-pair shim.
  const nextTier =
    nextSel.biologicalMother && nextSel.biologicalFather
      ? 'FAMILY_TRIO'
      : nextSel.biologicalMother
        ? 'FAMILY_DUO_MOTHER'
        : nextSel.biologicalFather
          ? 'FAMILY_DUO_FATHER'
          : 'PROBAND_ONLY'
  dispatch(setOrderType(nextTier))

  return effectiveNext.testType || null
}

/** @deprecated Prefer ``applySequencingOptionsChangeToCanonical`` (handles downgrades too). */
export const applySequencingComparatorUpgradeToCanonical = applySequencingOptionsChangeToCanonical
